import {
  ModelError_Item,
  patchV1CompaniesCompanyIdDepartmentsId,
  postV1CompaniesCompanyIdDepartments,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Department } from '~hooks/useDepartment/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import DepartmentForm, { DepartmentFormHandler } from './DepartmentForm';
import { DepartmentDto, DepartmentDtoAsyncError } from './departmentSchema';

interface DepartmentDialogProps {
  department?: Department;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const DepartmentDialog = observer(
  ({ department, isOpen, onClose, onSuccess }: DepartmentDialogProps) => {
    const { companyAssetsStore, toasterStore, userStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);
    const departmentFormRef = useRef<DepartmentFormHandler>(null);
    const [asyncErrors, setAsyncErrors] = useState<DepartmentDtoAsyncError[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const closeDialog = () => {
      setAsyncErrors([]);
      onClose();
    };

    const handleClose = () => {
      if (isSubmitting) {
        return;
      }

      closeDialog();
    };

    const handleSubmit = async () => {
      const formData = await departmentFormRef.current?.onSubmit();

      if (!formData) {
        return;
      }

      const departmentId = department?.id;

      try {
        setAsyncErrors([]);
        setIsSubmitting(true);

        if (departmentId) {
          const response = await patchV1CompaniesCompanyIdDepartmentsId({
            path: {
              'company-id': companyId,
              id: departmentId,
            },
            body: { name: formData.name },
          });
          const parsed = Department.parse(response.data.data);
          const name = parsed.name;

          companyAssetsStore.updateDepartment(parsed);
          toasterStore.push(
            alert(t('administration.department.updated', { name }), AlertTypes.success),
          );
        } else {
          const response = await postV1CompaniesCompanyIdDepartments({
            path: { 'company-id': formData.company.value },
            body: { name: formData.name },
          });
          const parsed = Department.parse(response.data.data);
          const name = parsed.name;

          companyAssetsStore.addDepartment(parsed);
          toasterStore.push(
            alert(t('administration.department.created', { name }), AlertTypes.success),
          );
        }

        setIsSubmitting(false);
        onSuccess?.();
        closeDialog();
      } catch (error) {
        setIsSubmitting(false);

        if (axios.isAxiosError(error)) {
          const errorsArray = (error.response?.data?.error?.errors ??
            []) as ModelError_Item[];

          const nextAsyncErrors: DepartmentDtoAsyncError[] = [];
          const formFieldInfoByFieldInError: Record<
            string,
            { fieldInForm: keyof DepartmentDto; label: string }
          > = {
            name: { fieldInForm: 'name', label: t('form_fields.name') },
          };

          errorsArray.forEach((errorsArrayItem) => {
            const { field, message } = errorsArrayItem;

            const isValueTakenError = message.includes('has already been taken');
            const fieldInfo = formFieldInfoByFieldInError[field];

            if (isValueTakenError && fieldInfo) {
              nextAsyncErrors.push({
                field: fieldInfo.fieldInForm,
                message: t('error_messages.field_taken', { field: fieldInfo.label }),
              });
            }
          });

          if (nextAsyncErrors.length) {
            setAsyncErrors(nextAsyncErrors);
          } else {
            connection.handleRequestError(
              error,
              departmentId
                ? t('error_messages.departments.failed_to_update')
                : t('error_messages.departments.failed_to_create'),
            );
          }
        } else {
          connection.handleRequestError(
            error,
            departmentId
              ? t('error_messages.departments.failed_to_update')
              : t('error_messages.departments.failed_to_create'),
          );
        }
      }
    };

    return (
      <Dialog isOpen={isOpen} preventCloseOnBackdropClick preventCloseOnEscapeKeyDown>
        <DialogHeader onClose={handleClose}>
          {department
            ? t('administration.department.update')
            : t('administration.department.create')}
        </DialogHeader>

        <DialogContent>
          <DepartmentForm
            ref={departmentFormRef}
            asyncErrors={asyncErrors}
            department={department}
          />
        </DialogContent>

        <DialogActions>
          {Boolean(asyncErrors.length) && (
            <ShakyErrorIndicator
              message={t('administration.users.user_form.invalid_data_error_messages')}
            />
          )}

          <SmallButton
            color="secondary"
            disabled={isSubmitting}
            onClick={handleClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton loading={isSubmitting} onClick={handleSubmit}>
            {department ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    );
  },
);

export default DepartmentDialog;
